import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ChartDateRange,
  ChartId,
  DashboardDateConfig,
  DimensionId,
  KpiId
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import { FilterNode } from '@blisspointmedia/bpm-types/dist/querying';
import { INIT_CUSTOM_PRESET_STATE } from '@blisspointmedia/bpm-types/dist/RelativeDatePresets';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import {
  dashboardDateConfigsEqual,
  resolveDashboardDateConfig,
  withComparisonSelection,
  withPrimarySelection
} from './date-config';
import { pickRange, toChartDateRange, toPickerRange } from './date-range';
import { ChartViewParams } from '../Charts/chart-view-params';
import {
  clampDashboardFilters,
  DashboardViewParams,
  effectiveDashboardFilters,
  fromDashboardViewSearch,
  initialDashboardFilters,
  linkFiltersStillRejected,
  withDashboardViewParams
} from './dashboard-view-params';

interface DashboardViewContextValue {
  // The dashboard's date window as the PICKER'S OWN state — what gets saved, and what the picker is
  // re-seeded from. `globalRange` is that resolved to concrete dates; the two move together.
  globalDateConfig: DashboardDateConfig | undefined;
  setGlobalDateConfig: (
    update: (previous: DashboardDateConfig | undefined) => DashboardDateConfig
  ) => void;
  // Resolve a saved config and apply it to the picker state AND both ranges at once — used to put
  // everything back on Discard changes.
  applyDateConfig: (config: DashboardDateConfig | undefined) => void;
  globalRange: ChartDateRange | undefined;
  setGlobalRange: (r: ChartDateRange | undefined) => void;
  perChartRange: Record<string, ChartDateRange>;
  setPerChartRange: (chartId: string, r: ChartDateRange | undefined) => void;
  resolveRange: (chartId: string) => ChartDateRange | undefined;
  // Comparison (period-over-period) date state, mirroring the primary triplet above. Only meaningful
  // for charts where resolveComparison(config) is defined; resolveComparisonRange returns undefined
  // when unset so the chart keeps its saved comparison window.
  globalComparisonRange: ChartDateRange | undefined;
  setGlobalComparisonRange: (r: ChartDateRange | undefined) => void;
  perChartComparisonRange: Record<string, ChartDateRange>;
  setPerChartComparisonRange: (chartId: string, r: ChartDateRange | undefined) => void;
  resolveComparisonRange: (chartId: string) => ChartDateRange | undefined;
  perChartKpi: Record<string, KpiId>;
  setPerChartKpi: (chartId: string, kpiId: KpiId | undefined) => void;
  resolveKpi: (chartId: string) => KpiId | undefined;
  // The metric and breakdown (color-split) picks made inside a card's chart. Like the KPI, these are
  // per-chart with no global equivalent. They live here rather than in MetricPickerChart's local
  // state because the card header has to READ them: expanding a card hands its whole on-screen view
  // to the focused view, and a pick the card can't see is a pick that gets dropped on the way.
  perChartMetric: Record<string, string>;
  setPerChartMetric: (chartId: string, colId: string | undefined) => void;
  resolveMetric: (chartId: string) => string | undefined;
  perChartBreakdown: Record<string, DimensionId>;
  setPerChartBreakdown: (chartId: string, dimId: DimensionId | undefined) => void;
  resolveBreakdown: (chartId: string) => DimensionId | undefined;
  // The filters on screen: a preview if set, else the URL's, else the saved default; `[]` is none.
  // `savedDashboardFilters` is that saved default, for "reset".
  dashboardFilters: FilterNode[];
  // `dashboardFilters` without the preview, as the URL has them: for anything that outlives the view.
  dashboardFiltersWithoutPreview: FilterNode[];
  setDashboardFilters: (next: FilterNode[]) => void;
  // Shown over `dashboardFilters` until cleared with `undefined`, but never written to the URL.
  setPreviewDashboardFilters: (next: FilterNode[] | undefined) => void;
  savedDashboardFilters: FilterNode[];
  // The link's filter params could not be read, so the dashboard opened on its saved filters; false
  // once other filters are shown or saved.
  dashboardFiltersRejected: boolean;
}

const NO_FILTERS: FilterNode[] = [];

// Transient (in-memory, non-persisted) view-override state for a dashboard: an optional global date
// range plus per-chart date, KPI, metric and breakdown overrides. resolveRange picks the date
// override first, then the global; the rest are per-chart only.
const DashboardViewContext = createContext<DashboardViewContextValue | undefined>(undefined);

// One per-chart override map — chartId -> value — plus its setter. Only `undefined` clears (an empty
// string is a value, not a clear), and clearing removes the key entirely rather than storing a hole,
// so a resolver reading the map falls all the way back: to the dashboard's global range, or to the
// chart's own saved value.
const usePerChartOverride = <T,>(initial: Record<string, T> = {}) => {
  const [overrides, setOverrides] = useState<Record<string, T>>(initial);
  const setOverride = useCallback((chartId: string, value: T | undefined) => {
    setOverrides(prev => {
      if (value === undefined) {
        const { [chartId]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [chartId]: value };
    });
  }, []);
  return [overrides, setOverride] as const;
};

interface DashboardViewProviderProps {
  children: ReactNode;
  // The window this dashboard was SAVED to open on, if it has one. Resolved against the company's
  // fiscal calendar at mount, so a saved "MTD" means THIS month every time it loads.
  savedDateConfig?: DashboardDateConfig;
  // Every chart actually placed on this dashboard right now — used once, at mount, to keep a stale
  // link (a chart since removed) or an early share (a chart not yet added) from seeding a phantom
  // per-chart override for an id that will never resolve.
  chartIds: ChartId[];
  // The filters this dashboard was SAVED with; the viewer starts on these unless the URL says
  // otherwise.
  savedDashboardFilters?: FilterNode[];
}

export const DashboardViewProvider = ({
  children,
  savedDateConfig,
  chartIds,
  savedDashboardFilters = NO_FILTERS
}: DashboardViewProviderProps) => {
  const { fiscal_cal: fiscalCal } = useCompanyInfo();
  // The view this dashboard was opened THROUGH, if any — a shared/reloaded link. Read once at mount:
  // from here on this component's state is the source of truth and the URL follows it (see the
  // mirror effect below), exactly as SingleChartView reads its own `openedWith` once.
  const [searchParams, setSearchParams] = useSearchParams();
  const [openedWith] = useState<DashboardViewParams>(() => fromDashboardViewSearch(searchParams));

  // Filtered, once, to charts that actually exist on this dashboard right now (see the prop comment
  // above). Computed alongside the state it seeds rather than inside usePerChartOverride, so all
  // five per-chart maps below share one filtering pass over `openedWith.perChart`.
  const [seededPerChart] = useState(() => {
    const allowed = new Set<string>(chartIds);
    const seedField = <T,>(pick: (p: ChartViewParams) => T | undefined): Record<string, T> => {
      const seeded: Record<string, T> = {};
      Object.entries(openedWith.perChart).forEach(([chartId, params]) => {
        if (!allowed.has(chartId)) return;
        const value = pick(params);
        if (value !== undefined) {
          seeded[chartId] = value;
        }
      });
      return seeded;
    };
    return {
      range: seedField<ChartDateRange>(p => p.dateRange),
      comparisonRange: seedField<ChartDateRange>(p => p.comparisonRange),
      kpi: seedField<KpiId>(p => p.kpiId),
      metric: seedField<string>(p => p.metric),
      breakdown: seedField<DimensionId>(p => p.breakdown)
    };
  });

  // An explicit URL link beats the dashboard's saved default. The primary half decides which source
  // wins for BOTH the config and the resolved range together — never one from the URL and the other
  // from the saved config — because disagreeing sources are exactly the bug this file's history
  // already hit once: the chip would claim a preset ("MTD") while the data showed the link's window.
  // The comparison half rides along with the primary: present in the URL, it's hand-picked the same
  // way; absent, it stays fully unset rather than falling back to the saved config's comparison,
  // which is defined relative to the SAVED primary and would silently disagree with the URL's.
  const [globalDateConfig, setGlobalDateConfigState] = useState<DashboardDateConfig | undefined>(
    () => {
      if (!openedWith.globalDateRange) return savedDateConfig;
      let built = withPrimarySelection(undefined, toPickerRange(openedWith.globalDateRange), {
        selectedPreset: null,
        startCustomPreset: INIT_CUSTOM_PRESET_STATE,
        endCustomPreset: INIT_CUSTOM_PRESET_STATE
      });
      if (openedWith.globalComparisonRange) {
        built = withComparisonSelection(built, toPickerRange(openedWith.globalComparisonRange), {
          selectedCompPerType: null,
          startCompCustomPreset: INIT_CUSTOM_PRESET_STATE,
          endCompCustomPreset: INIT_CUSTOM_PRESET_STATE
        });
      }
      return built;
    }
  );
  // The dashboard's own saved window when it has one (and the URL carried no override); otherwise a
  // real "last 30 days" so every linked chart uniformly follows the global range from load — rather
  // than each chart sitting on its own saved range (which made the picker label disagree with the
  // actual data, and let a wide saved range pull in outliers). The user can change it; per-chart
  // pickers still override it.
  const [globalRange, setGlobalRange] = useState<ChartDateRange | undefined>(() => {
    if (openedWith.globalDateRange) return openedWith.globalDateRange;
    const { range } = resolveDashboardDateConfig(savedDateConfig, fiscalCal);
    return toChartDateRange(range ?? toPickerRange(undefined));
  });
  const [perChartRange, setPerChartRange] = usePerChartOverride<ChartDateRange>(
    seededPerChart.range
  );
  // Comparison window defaults to undefined (no global comparison override) — a comparison chart
  // keeps its own saved comparison until the user sets one from a picker, or the dashboard itself
  // was saved with one, or the URL carried one.
  const [globalComparisonRange, setGlobalComparisonRange] = useState<ChartDateRange | undefined>(
    () => {
      if (openedWith.globalDateRange) return openedWith.globalComparisonRange;
      const { comparisonRange } = resolveDashboardDateConfig(savedDateConfig, fiscalCal);
      return comparisonRange ? toChartDateRange(comparisonRange) : undefined;
    }
  );
  const [perChartComparisonRange, setPerChartComparisonRange] = usePerChartOverride<ChartDateRange>(
    seededPerChart.comparisonRange
  );
  const [perChartKpi, setPerChartKpi] = usePerChartOverride<KpiId>(seededPerChart.kpi);
  const [perChartMetric, setPerChartMetric] = usePerChartOverride<string>(seededPerChart.metric);
  const [perChartBreakdown, setPerChartBreakdown] = usePerChartOverride<DimensionId>(
    seededPerChart.breakdown
  );

  const [dashboardFiltersState, setDashboardFiltersState] = useState<FilterNode[]>(() =>
    initialDashboardFilters(openedWith.dashboardFilters, savedDashboardFilters)
  );
  const [previewDashboardFilters, setPreviewDashboardFiltersState] = useState<FilterNode[]>();
  const [savedFiltersWhenOpened] = useState(savedDashboardFilters);
  const dashboardFiltersRejected = linkFiltersStillRejected({
    rejected: openedWith.dashboardFiltersRejected ?? false,
    openedSaved: savedFiltersWhenOpened,
    state: dashboardFiltersState,
    preview: previewDashboardFilters,
    saved: savedDashboardFilters
  });
  const setDashboardFilters = useCallback(
    (next: FilterNode[]) => setDashboardFiltersState(clampDashboardFilters(next)),
    []
  );
  const setPreviewDashboardFilters = useCallback(
    (next: FilterNode[] | undefined) =>
      setPreviewDashboardFiltersState(next && clampDashboardFilters(next)),
    []
  );
  const {
    shown: dashboardFilters,
    withoutPreview: dashboardFiltersWithoutPreview,
    forUrl: dashboardFiltersInUrl
  } = useMemo(
    () =>
      effectiveDashboardFilters({
        state: dashboardFiltersState,
        preview: previewDashboardFilters,
        saved: savedDashboardFilters
      }),
    [dashboardFiltersState, previewDashboardFilters, savedDashboardFilters]
  );

  // Functional-only: the picker reports its primary and comparison halves through separate callbacks
  // that can BOTH fire on one Apply, so a caller merging into a value it read during render would
  // write the second change over a stale copy of the first.
  const setGlobalDateConfig = useCallback(
    (update: (previous: DashboardDateConfig | undefined) => DashboardDateConfig) =>
      setGlobalDateConfigState(update),
    []
  );

  const applyDateConfig = useCallback(
    (config: DashboardDateConfig | undefined) => {
      const { range, comparisonRange } = resolveDashboardDateConfig(config, fiscalCal);
      setGlobalDateConfigState(config);
      setGlobalRange(toChartDateRange(range ?? toPickerRange(undefined)));
      setGlobalComparisonRange(comparisonRange ? toChartDateRange(comparisonRange) : undefined);
    },
    [fiscalCal]
  );

  // Mirror current state back into the URL, so a reload and a copied link carry the same view — the
  // dashboard-level counterpart of SingleChartView's own mirror effect (see its comment for why this
  // has to be an EFFECT rather than a write inside each picker handler: one Apply on the date picker
  // fires the primary and comparison callbacks back-to-back, so a handler merging its own change into
  // the previous render's state would write the second over a stale copy of the first). `replace:
  // true` and a same-string no-op guard for the same reasons as there: a picker change isn't a step
  // the back button should have to walk back through, and the guard keeps this from looping against
  // the searchParams identity it depends on.
  //
  // Only a REAL deviation from the dashboard's saved default becomes a global date param — see
  // dashboardDateConfigsEqual above for why this is a value compare, not `!==`. The per-chart maps
  // need no such gate: usePerChartOverride already deletes a key on clear, so they're deviations-only
  // for free, and every entry present is one worth carrying.
  useEffect(() => {
    const dateIsDefault = dashboardDateConfigsEqual(globalDateConfig, savedDateConfig);

    const perChartIds = new Set<string>([
      ...Object.keys(perChartRange),
      ...Object.keys(perChartComparisonRange),
      ...Object.keys(perChartKpi),
      ...Object.keys(perChartMetric),
      ...Object.keys(perChartBreakdown)
    ]);
    const perChart: Record<string, ChartViewParams> = {};
    perChartIds.forEach(chartId => {
      perChart[chartId] = {
        dateRange: perChartRange[chartId],
        comparisonRange: perChartComparisonRange[chartId],
        kpiId: perChartKpi[chartId],
        metric: perChartMetric[chartId],
        breakdown: perChartBreakdown[chartId]
      };
    });

    const next = withDashboardViewParams(searchParams, {
      globalDateRange: dateIsDefault ? undefined : globalRange,
      globalComparisonRange: dateIsDefault ? undefined : globalComparisonRange,
      perChart,
      dashboardFilters: dashboardFiltersInUrl
    });
    if (next.toString() === searchParams.toString()) return;
    setSearchParams(next, { replace: true });
  }, [
    searchParams,
    setSearchParams,
    globalDateConfig,
    savedDateConfig,
    globalRange,
    globalComparisonRange,
    perChartRange,
    perChartComparisonRange,
    perChartKpi,
    perChartMetric,
    perChartBreakdown,
    dashboardFiltersInUrl
  ]);

  const resolveRange = useCallback(
    (chartId: string) => pickRange(chartId, perChartRange, globalRange),
    [perChartRange, globalRange]
  );

  const resolveComparisonRange = useCallback(
    (chartId: string) => pickRange(chartId, perChartComparisonRange, globalComparisonRange),
    [perChartComparisonRange, globalComparisonRange]
  );

  const resolveKpi = useCallback((chartId: string) => perChartKpi[chartId], [perChartKpi]);

  const resolveMetric = useCallback((chartId: string) => perChartMetric[chartId], [perChartMetric]);

  const resolveBreakdown = useCallback(
    (chartId: string) => perChartBreakdown[chartId],
    [perChartBreakdown]
  );

  const value = useMemo(
    () => ({
      globalDateConfig,
      setGlobalDateConfig,
      applyDateConfig,
      globalRange,
      setGlobalRange,
      perChartRange,
      setPerChartRange,
      resolveRange,
      globalComparisonRange,
      setGlobalComparisonRange,
      perChartComparisonRange,
      setPerChartComparisonRange,
      resolveComparisonRange,
      perChartKpi,
      setPerChartKpi,
      resolveKpi,
      perChartMetric,
      setPerChartMetric,
      resolveMetric,
      perChartBreakdown,
      setPerChartBreakdown,
      resolveBreakdown,
      dashboardFilters,
      dashboardFiltersWithoutPreview,
      setDashboardFilters,
      setPreviewDashboardFilters,
      savedDashboardFilters,
      dashboardFiltersRejected
    }),
    [
      globalDateConfig,
      setGlobalDateConfig,
      applyDateConfig,
      globalRange,
      perChartRange,
      setPerChartRange,
      resolveRange,
      globalComparisonRange,
      perChartComparisonRange,
      setPerChartComparisonRange,
      resolveComparisonRange,
      perChartKpi,
      setPerChartKpi,
      resolveKpi,
      perChartMetric,
      setPerChartMetric,
      resolveMetric,
      perChartBreakdown,
      setPerChartBreakdown,
      resolveBreakdown,
      dashboardFilters,
      dashboardFiltersWithoutPreview,
      setDashboardFilters,
      setPreviewDashboardFilters,
      savedDashboardFilters,
      dashboardFiltersRejected
    ]
  );

  return <DashboardViewContext.Provider value={value}>{children}</DashboardViewContext.Provider>;
};

export const useDashboardView = () => {
  const context = useContext(DashboardViewContext);
  if (!context) {
    throw new Error('useDashboardView must be used within a DashboardViewProvider');
  }
  return context;
};
